// Strict CSP utilities for lite/offline modes
import { isFeatureEnabled } from '@/config/featureFlags';

interface CSPDirectives {
  'default-src': string[];
  'script-src': string[];
  'style-src': string[];
  'img-src': string[];
  'media-src': string[];
  'connect-src': string[];
  'font-src': string[];
  'frame-ancestors': string[];
  'base-uri': string[];
  'object-src': string[];
  'form-action': string[];
}

// Base CSP directives for normal mode
const BASE_CSP: CSPDirectives = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'wasm-unsafe-eval'"],
  'style-src': ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com"],
  'img-src': ["'self'", "data:", "https://res.cloudinary.com", "https://cxplnvfimnyjiyhjwvnb.supabase.co"],
  'media-src': ["'self'", "https://res.cloudinary.com"],
  'connect-src': ["'self'", "https://cxplnvfimnyjiyhjwvnb.supabase.co", "https://res.cloudinary.com", "https://fonts.googleapis.com", "https://fonts.gstatic.com"],
  'font-src': ["'self'", "data:", "https://fonts.gstatic.com"],
  'frame-ancestors': ["'none'"],
  'base-uri': ["'self'"],
  'object-src': ["'none'"],
  'form-action': ["'self'"]
};

// Stricter CSP directives for lite/offline mode
const STRICT_CSP: CSPDirectives = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'wasm-unsafe-eval'"],
  'style-src': ["'self'", "'unsafe-inline'"], // Remove Google Fonts
  'img-src': ["'self'", "data:", "https://res.cloudinary.com", "https://cxplnvfimnyjiyhjwvnb.supabase.co"],
  'media-src': ["'self'"], // Remove Cloudinary for media in strict mode
  'connect-src': ["'self'", "https://cxplnvfimnyjiyhjwvnb.supabase.co"], // Only essential Supabase connection
  'font-src': ["'self'", "data:"], // Remove Google Fonts CDN
  'frame-ancestors': ["'none'"],
  'base-uri': ["'self'"],
  'object-src': ["'none'"],
  'form-action': ["'self'"]
};

export function generateCSPString(cspDirectives: CSPDirectives): string {
  const directives = Object.entries(cspDirectives)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; ');
  
  return directives + '; upgrade-insecure-requests;';
}

export function getCSPForMode(mode: 'full' | 'balanced' | 'lite' | 'offline'): string {
  // Don't apply strict CSP if user is in full or balanced mode
  if (isFeatureEnabled('STRICT_CSP_FOR_LITE_MODE') && (mode === 'lite' || mode === 'offline')) {
    console.debug('[CSP] Applying strict CSP for mode:', mode);
    return generateCSPString(STRICT_CSP);
  }
  
  return generateCSPString(BASE_CSP);
}

export function applyDynamicCSP(mode: 'full' | 'balanced' | 'lite' | 'offline'): void {
  if (!isFeatureEnabled('STRICT_CSP_FOR_LITE_MODE')) {
    return;
  }

  try {
    const existingCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (existingCSP) {
      // Only apply strict CSP for lite and offline modes
      const csp = (mode === 'lite' || mode === 'offline') ? getCSPForMode(mode) : getCSPForMode('full');
      existingCSP.setAttribute('content', csp);
      console.debug('[CSP] Applied CSP for', mode, 'mode');
      
      document.documentElement.setAttribute('data-csp-mode', mode);
      document.documentElement.removeAttribute('data-strict-csp-pending');
    }
  } catch (error) {
    console.warn('[CSP] Failed to apply dynamic CSP:', error);
  }
}

// Check if strict CSP should be applied based on pending flag from initial script
export function checkAndApplyPendingStrictCSP(): void {
  if (!isFeatureEnabled('STRICT_CSP_FOR_LITE_MODE')) {
    return;
  }

  const hasPendingStrictCSP = document.documentElement.hasAttribute('data-strict-csp-pending');
  if (hasPendingStrictCSP) {
    // Determine mode from DOM state
    const isLiteClass = document.documentElement.classList.contains('lite');
    const isOffline = !navigator.onLine;
    const currentMode = localStorage.getItem('mode') as 'full' | 'balanced' | 'lite' | 'offline' | null;
    
    let mode: 'full' | 'balanced' | 'lite' | 'offline' = 'full';
    if (isOffline || currentMode === 'offline') {
      mode = 'offline';
    } else if (isLiteClass || currentMode === 'lite') {
      mode = 'lite';
    } else if (currentMode === 'balanced') {
      mode = 'balanced';
    }
    
    applyDynamicCSP(mode);
  }
}